import styled from 'styled-components';
import type { LanguageValue } from '@elfsight/configurator-contracts';
import { Show } from '@elfsight/lib-utils';
import type { PriceProps } from '@elfsight/widget-ui';
import { getTokenFontStyle, Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useCheckoutColors, useCheckoutI18n } from '../providers';
import { InvalidPrice } from './invalid-price';

const COMPONENT_NAME = 'total';

export interface TotalProps {
  label: string;
  price?: PriceProps;
}

export function Total({ label, price }: TotalProps) {
  const { language } = useSettings<{ language: LanguageValue }>();
  const { textColor } = useCheckoutColors();
  const { PriceComponent } = useCheckoutI18n();

  const withPrice = !!price;

  const priceFont = getTokenFontStyle('text18Bold');

  return (
    <Component $textColor={textColor}>
      <Typography
        textToken="text18Bold"
        className={createReadableClass({
          component: COMPONENT_NAME,
          element: 'label'
        })}
      >
        {label}
      </Typography>
      <Show when={withPrice}>
        <PriceComponent
          {...price!}
          language={language}
          valueColor={textColor}
          affixColor={textColor}
          valueFont={priceFont}
          affixFont={priceFont}
          className={createReadableClass({
            component: COMPONENT_NAME,
            element: 'price'
          })}
          style={{ display: 'inline-flex', width: 'fit-content' }}
        />
      </Show>
      <Show when={!withPrice}>
        <InvalidPrice componentName={COMPONENT_NAME} textToken="text18Bold" />
      </Show>
    </Component>
  );
}

const Component = styled.div.attrs<{ $textColor: string }>({
  className: createReadableClass({
    component: COMPONENT_NAME
  })
})`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  gap: 4px 16px;

  width: 100%;

  color: ${({ $textColor }) => $textColor};
`;
